import cloneDeep from 'lodash/cloneDeep';
import { MOCK_PLUGIN_ID_PREFIX } from '../../consts';
import { formPluginUrl } from '../../utils/routes';
import { PluginlistServiceOutput } from './PluginListServiceOutput';
import { PluginListStore } from './PluginListStore';
const getNextId = (slots) => {
    const keys = Object.keys(slots);
    if (keys.length === 0) {
        return 1;
    }
    return keys.length + 1;
};
export class PluginListService {
    static instance;
    static getInstance() {
        if (!PluginListService.instance) {
            PluginListService.instance = new PluginListService();
        }
        return PluginListService.instance;
    }
    static getOutput() {
        PluginListService.getInstance();
        return PluginlistServiceOutput.getReactiveOutput();
    }
    static getNotificationInterface() {
        return PluginListService.getInstance();
    }
    state = {
        slots: {
            sidebar: {},
            bottom1: {},
            bottom2: {},
            bottom3: {},
            buttonstray: {},
            overlay: {},
            components: {},
        },
        plugins: [],
    };
    output;
    store = new PluginListStore();
    constructor() {
        this.output = new PluginlistServiceOutput();
        this.notifyAddPlugin(formPluginUrl('chat'));
        const mockPluginsCount = Number(import.meta.env.DEV_MOCK_PLUGINS_COUNT);
        if (!Number.isNaN(mockPluginsCount) && mockPluginsCount > 0) {
            for (let i = 0; i < mockPluginsCount; i++) {
                const id = `${MOCK_PLUGIN_ID_PREFIX}${i}`;
                this.notifyAddPlugin(formPluginUrl(id));
            }
        }
    }
    addPlugin(plugin) {
        if (this.state.plugins.find((p) => p.id === plugin.id)) {
            console.error(`Plugin ${plugin.id} already exists`);
            return;
        }
        this.state.plugins.push(plugin);
        this.addToSlots(plugin);
        this.publishOutput();
        this.persist();
    }
    addToSlots(plugin) {
        const frames = plugin.frames;
        if (!frames) {
            return;
        }
        for (const frame of frames.filter((f) => f.type === 'panel')) {
            const id = String(getNextId(this.state.slots[frame.slot]));
            switch (frame.slot) {
                case 'sidebar':
                    this.state.slots.sidebar[id] = {
                        ...frame,
                        pluginId: plugin.id,
                    };
                    break;
                case 'bottom1':
                    this.state.slots.bottom1[id] = {
                        ...frame,
                        pluginId: plugin.id,
                    };
                    break;
                case 'bottom2':
                    this.state.slots.bottom2[id] = {
                        ...frame,
                        pluginId: plugin.id,
                    };
                    break;
                case 'bottom3':
                    this.state.slots.bottom3[id] = {
                        ...frame,
                        pluginId: plugin.id,
                    };
                    break;
                default:
                    break;
            }
        }
        for (const frame of frames.filter((f) => f.type === 'overlay')) {
            const id = String(getNextId(this.state.slots.overlay));
            this.state.slots.overlay[id] = {
                ...frame,
                pluginId: plugin.id,
            };
        }
        for (const frame of frames.filter((f) => f.type === 'component')) {
            const id = String(getNextId(this.state.slots.components));
            this.state.slots.components[id] = {
                ...frame,
                pluginId: plugin.id,
            };
        }
        for (const frame of frames.filter((f) => f.type === 'button')) {
            const id = String(getNextId(this.state.slots.buttonstray));
            this.state.slots.buttonstray[id] = {
                ...frame,
                pluginId: plugin.id,
            };
        }
    }
    removePlugin(plugin) {
        const index = this.state.plugins.findIndex((p) => p.id === plugin.id);
        if (index !== -1) {
            this.state.plugins.splice(index, 1);
        }
        this.removeFromSlots(plugin);
        this.publishOutput();
    }
    getPlugins() {
        return this.state.plugins;
    }
    movePlugin(pluginid, toPositionInList) {
        const index = this.state.plugins.findIndex((p) => p.id === pluginid);
        if (index === -1) {
            return;
        }
        const item = this.state.plugins.splice(index, 1)[0];
        this.state.plugins.splice(toPositionInList, 0, item);
        this.publishOutput();
    }
    notifyAddPlugin(url) {
        const base = url.endsWith('/') ? url : `${url}/`;
        const pluginJsonUrl = new URL('streamrtv-plugin.json', base).toString();
        fetch(pluginJsonUrl, { headers: { Accept: 'application/json' } })
            .then(async (res) => {
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(`Failed to fetch plugin manifest (${res.status} ${res.statusText}). Body preview: ${text.slice(0, 200)}`);
            }
            const contentType = res.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const jsonResponse = await res.json();
                console.log('jsonResponse', jsonResponse);
                this.addPlugin(jsonResponse);
                return;
            }
            const text = await res.text();
            try {
                const parsed = JSON.parse(text);
                console.log('parsed', parsed);
                this.addPlugin(parsed);
            }
            catch (_e) {
                throw new Error(`Expected JSON from ${pluginJsonUrl} but received content-type "${contentType}". Body preview: ${text.slice(0, 200)}`);
            }
        })
            .catch((err) => {
            console.error(err);
        });
    }
    removeFromSlots(plugin) {
        const pluginId = plugin.id;
        // Filter out frames belonging to this plugin from all slots
        const removeByPluginId = (record) => {
            for (const key of Object.keys(record)) {
                if (record[key]?.pluginId === pluginId) {
                    delete record[key];
                }
            }
        };
        removeByPluginId(this.state.slots.sidebar);
        removeByPluginId(this.state.slots.bottom1);
        removeByPluginId(this.state.slots.bottom2);
        removeByPluginId(this.state.slots.bottom3);
        removeByPluginId(this.state.slots.overlay);
        removeByPluginId(this.state.slots.components);
        removeByPluginId(this.state.slots.buttonstray);
    }
    publishOutput() {
        // need to pass different object reference so that zustand does the state change
        // TODO: why we need cloneDeep(), simple clone() should work ok?
        this.output.setOutput(cloneDeep(this.state));
    }
    toJson() {
        return JSON.stringify(this.state);
    }
    fromJson(json) {
        const state = JSON.parse(json);
        this.state = state;
        this.publishOutput();
    }
    persist() {
        this.store.save(this.state);
    }
}
